import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, X, ArrowLeft, ArrowRight, Quote, FileText, Wallet, RefreshCw } from "lucide-react";
import { SEO, SITE_URL } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";
import { CLUB_PRESENTATIONS } from "../data/content";

export default function ClubDetail() {
  const { slug } = useParams();
  const { data: club, loading, notFound } = useContent(`/clubs/${slug}`, [slug]);
  if (loading) return <PageLoader />;
  if (notFound || !club) return <Navigate to="/travel-club-reviews" replace />;

  const next = club.next;
  const presentation = CLUB_PRESENTATIONS[club.slug];

  const reviewLd = {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: { "@type": "Organization", name: club.name },
    reviewRating: {
      "@type": "Rating",
      ratingValue: club.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: { "@type": "Organization", name: "Travel Club Navigator Group" },
    publisher: { "@type": "Organization", name: "Travel Club Navigator Group" },
    reviewBody: club.summary,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Travel Club Reviews", item: `${SITE_URL}/travel-club-reviews` },
      { "@type": "ListItem", position: 3, name: club.name, item: `${SITE_URL}/travel-club-reviews/${club.slug}` },
    ],
  };
  const faqLd = club.faqs && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: club.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const memberReviews = club.memberReviews || [];
  const memberReviewsLd = memberReviews.length && {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: club.name,
    review: memberReviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      datePublished: r.date,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      name: r.title,
      reviewBody: r.body,
    })),
  };
  const jsonLd = [
    reviewLd,
    breadcrumbLd,
    ...(faqLd ? [faqLd] : []),
    ...(memberReviewsLd ? [memberReviewsLd] : []),
  ];

  return (
    <>
      <SEO
        title={club.seoTitle || `${club.name} Review`}
        description={club.metaDescription || `${club.name} review — ${club.verdict}. ${club.summary}`}
        path={`/travel-club-reviews/${club.slug}`}
        image={club.image}
        keywords={club.keywords}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative flex min-h-[60vh] items-end overflow-hidden">
        <img src={club.image} alt={club.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-14 pt-32 lg:px-8">
          <Link
            to="/travel-club-reviews"
            data-testid="club-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All club reviews
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-sm font-bold text-navy backdrop-blur">
                <Star className="h-4 w-4 fill-accent text-accent" />
                {club.rating.toFixed(1)} / 5
              </span>
              <span className="rounded-full bg-primary px-3.5 py-1.5 text-sm font-semibold text-white">
                {club.verdict}
              </span>
              {club.partner && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3.5 py-1.5 text-sm font-bold text-navy">
                  <Star className="h-4 w-4 fill-navy text-navy" /> Featured Partner
                </span>
              )}
            </div>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              {club.name}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-slate-200">{club.tagline}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        {club.partner && (
          <Reveal>
            <div className="mb-10 flex items-start gap-3 rounded-2xl border border-accent/30 bg-accent/10 p-5">
              <Star className="mt-0.5 h-5 w-5 flex-none fill-accent text-accent" />
              <p className="text-sm leading-relaxed text-navy">
                <strong>Featured Partner disclosure.</strong> {club.name} is a paid partner of
                Travel Club Navigator. This placement is sponsored — but our ratings and honest
                pros/cons are editorially independent and unchanged.
              </p>
            </div>
          </Reveal>
        )}

        {/* Overview */}
        <Reveal>
          <Overline>The Overview</Overline>
          <p className="mt-4 font-serif-accent text-2xl leading-relaxed text-navy sm:text-3xl">
            {club.summary}
          </p>
        </Reveal>

        {/* Scores */}
        <Reveal delay={0.05}>
          <div className="mt-12 grid gap-6 rounded-3xl card-3d p-8 sm:grid-cols-2">
            {club.scores.map((s) => (
              <div key={s.label}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-navy">{s.label}</span>
                  <span className="font-display text-lg font-bold text-secondary">{s.value}</span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-secondary to-primary"
                    style={{ width: `${s.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Cost structure */}
        {club.costStructure && (
          <Reveal delay={0.05}>
            <div className="mt-14" data-testid="club-cost-structure">
              <Overline>How the Cost Works</Overline>
              <p className="mt-4 max-w-3xl font-serif-accent text-xl leading-relaxed text-navy sm:text-2xl">
                {club.costStructure.intro}
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div className="rounded-3xl border border-secondary/20 bg-secondary/5 p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
                    <Wallet className="h-5 w-5" />
                  </span>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {club.costStructure.upfront.label}
                  </p>
                  <p className="mt-1 font-display text-2xl font-black text-navy">
                    {club.costStructure.upfront.amount}
                  </p>
                  <p className="mt-3 leading-relaxed text-slate-600">
                    {club.costStructure.upfront.desc}
                  </p>
                </div>
                <div className="rounded-3xl border border-primary/20 bg-primary/5 p-8">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                    <RefreshCw className="h-5 w-5" />
                  </span>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {club.costStructure.annual.label}
                  </p>
                  <p className="mt-1 font-display text-2xl font-black text-navy">
                    {club.costStructure.annual.amount}
                  </p>
                  <p className="mt-3 leading-relaxed text-slate-600">
                    {club.costStructure.annual.desc}
                  </p>
                </div>
              </div>
              {club.costStructure.note && (
                <p className="mt-5 max-w-3xl text-sm leading-relaxed text-slate-500">
                  {club.costStructure.note}
                </p>
              )}
            </div>
          </Reveal>
        )}

        {/* Pros & Cons */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-secondary/20 bg-secondary/5 p-8">
              <h3 className="font-display text-xl font-bold text-navy">What we liked</h3>
              <ul className="mt-5 space-y-3">
                {club.pros.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-none text-secondary" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-3xl border border-primary/20 bg-primary/5 p-8">
              <h3 className="font-display text-xl font-bold text-navy">Watch out for</h3>
              <ul className="mt-5 space-y-3">
                {club.cons.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-slate-700">
                    <X className="mt-0.5 h-5 w-5 flex-none text-primary" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Bottom line */}
        <Reveal delay={0.05}>
          <div className="mt-8 flex gap-5 rounded-3xl bg-navy p-8 text-white sm:p-10">
            <Quote className="h-10 w-10 flex-none text-accent" />
            <div>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-accent">
                The Bottom Line
              </h3>
              <p className="mt-3 font-serif-accent text-xl leading-relaxed sm:text-2xl">
                {club.bottomLine}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Member Reviews */}
        {memberReviews.length > 0 && (
          <Reveal delay={0.05}>
            <div className="mt-14" data-testid="club-member-reviews">
              <Overline>From the Community</Overline>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                {club.name}: Member Reviews
              </h2>
              {club.reviewDisclosure && (
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
                  {club.reviewDisclosure}
                </p>
              )}
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {memberReviews.map((r, i) => (
                  <div
                    key={`${r.name}-${i}`}
                    data-testid={`member-review-${club.slug}-${i}`}
                    className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                  >
                    <div className="flex items-center gap-1" aria-label={`${r.rating} out of 5 stars`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`h-4 w-4 ${
                            n <= r.rating ? "fill-accent text-accent" : "text-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold text-navy">{r.title}</h3>
                    <p className="mt-2 flex-1 leading-relaxed text-slate-600">"{r.body}"</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
                      <span className="font-semibold text-navy">{r.name}</span>
                      {r.location && <span>• {r.location}</span>}
                      {r.memberSince && <span>• Member since {r.memberSince}</span>}
                      {r.date && <span>• {r.date}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {/* FAQ */}
        {club.faqs && (
          <Reveal delay={0.05}>
            <div className="mt-14" data-testid="club-faq">
              <Overline>Frequently Asked</Overline>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                {club.name}: Your Questions, Answered
              </h2>
              <div className="mt-8 space-y-4">
                {club.faqs.map((f) => (
                  <div
                    key={f.q}
                    data-testid={`faq-item-${club.slug}`}
                    className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                  >
                    <h3 className="font-display text-lg font-bold text-navy">{f.q}</h3>
                    <p className="mt-2 leading-relaxed text-slate-600">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {presentation && (
          <Reveal delay={0.05}>
            <Link
              to={`/travel-club-reviews/${club.slug}/presentation`}
              data-testid="club-presentation-link"
              className="group mt-8 flex flex-col items-start justify-between gap-4 rounded-3xl bg-navy p-8 sm:flex-row sm:items-center"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-white/10 text-accent">
                  <FileText className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold text-white">
                    What to Expect at an {club.name} Presentation
                  </h3>
                  <p className="mt-1 text-sm text-slate-300">
                    A consumer-first breakdown of the sales pitch, common tactics, and how to protect yourself.
                  </p>
                </div>
              </div>
              <span className="inline-flex flex-none items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
                Read the Guide
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        )}

        {/* Next */}
        <Reveal delay={0.05}>
          <Link
            to={`/travel-club-reviews/${next.slug}`}
            data-testid="club-next-link"
            className="group mt-10 flex items-center justify-between rounded-3xl card-3d p-6"
          >
            <span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Next review
              </span>
              <span className="mt-1 block font-display text-xl font-bold text-navy">
                {next.name}
              </span>
            </span>
            <ArrowRight className="h-6 w-6 text-primary transition-transform duration-200 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
